import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const Recursion: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('recursion');
  const videoRef = useVideoProgress('recursion');

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>

        {/* Title and description */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Recursion</h1>
          <p className="text-white/70 text-lg">Master recursive problem-solving techniques and their applications in algorithms.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What is Recursion?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Recursion is a programming technique where a function solves a problem by breaking it into smaller, self-similar subproblems, 
                calling itself until a base condition is met. It's a powerful approach rooted in mathematical induction, ideal for hierarchical or repetitive tasks.
              </p>
              <p>
                Think of recursion like a set of Russian nesting dolls: each doll contains a smaller version of itself, 
                and you keep opening them until you reach the smallest one. In code, this translates to breaking down complex problems into manageable pieces.
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Core Principles</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Self-Reference:</strong> Function calls itself</li>
                  <li><strong>Base Case:</strong> Termination condition</li>
                  <li><strong>Progression:</strong> Moves toward termination</li>
                  <li><strong>Stack Management:</strong> Tracks recursive calls</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Core Components */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Core Components</h2>
            <div className="space-y-6">
              {/* Base Case */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Base Case</h3>
                  <div className="text-2xl">🛑</div>
                </div>
                <p className="text-white/70 mb-4">
                  The condition that stops recursion, preventing infinite calls. Every recursive function needs at least one base case.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Factorial with base case
function factorial(n: number): number {
  if (n <= 1) return 1; // Base case
  return n * factorial(n - 1); // Recursive case
}

// Multiple base cases in Fibonacci
function fibonacci(n: number): number {
  if (n === 0) return 0; // Base case 1
  if (n === 1) return 1; // Base case 2
  return fibonacci(n - 1) + fibonacci(n - 2);
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Recursive Case */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Recursive Case</h3>
                  <div className="text-2xl">🔄</div>
                </div>
                <p className="text-white/70 mb-4">
                  The part where the function calls itself with a modified input, reducing the problem size toward the base case.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Binary Search recursive case
function binarySearch(
  arr: number[], 
  target: number,
  left: number,
  right: number
): number {
  if (left > right) return -1; // Base case
  
  const mid = Math.floor((left + right) / 2);
  if (arr[mid] === target) return mid; // Base case
  
  // Recursive cases
  if (arr[mid] > target) {
    return binarySearch(arr, target, left, mid - 1);
  }
  return binarySearch(arr, target, mid + 1, right);
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Advanced Techniques */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Advanced Techniques</h2>
            <div className="space-y-6">
              {/* Tail Recursion */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Tail Recursion</h3>
                  <div className="text-2xl">➡️</div>
                </div>
                <p className="text-white/70 mb-4">
                  A recursive call is "tail" if it's the last operation, enabling compilers to optimize it into a loop.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Non-tail recursive factorial
function factorial(n: number): number {
  if (n <= 1) return 1;
  return n * factorial(n - 1); // Multiplication after recursion
}

// Tail recursive factorial
function factorialTail(n: number, acc: number = 1): number {
  if (n <= 1) return acc;
  return factorialTail(n - 1, n * acc); // Last operation
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Memoization */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Memoization</h3>
                  <div className="text-2xl">💾</div>
                </div>
                <p className="text-white/70 mb-4">
                  Caches results of expensive recursive calls to avoid redundant computations.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Memoized Fibonacci
function fibMemo(n: number, memo: Map<number, number> = new Map()): number {
  if (n <= 1) return n;
  if (memo.has(n)) return memo.get(n)!;
  
  const result = fibMemo(n - 1, memo) + fibMemo(n - 2, memo);
  memo.set(n, result);
  return result;
}

console.log(fibMemo(50)); // Much faster than naive recursion`}</code>
                  </pre>
                </div>
              </div>

              {/* Dynamic Programming */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Dynamic Programming</h3>
                  <div className="text-2xl">📊</div>
                </div>
                <p className="text-white/70 mb-4">
                  Combines recursion with overlapping subproblems and optimal substructure for efficiency.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Top-down DP for knapsack
function knapsack(
  values: number[],
  weights: number[],
  capacity: number,
  n: number,
  memo: Map<string, number> = new Map()
): number {
  if (n === 0 || capacity === 0) return 0;
  const key = \`\${n},\${capacity}\`;
  if (memo.has(key)) return memo.get(key)!;
  
  if (weights[n - 1] > capacity) {
    return knapsack(values, weights, capacity, n - 1, memo);
  }
  
  const include = values[n - 1] + knapsack(values, weights, capacity - weights[n - 1], n - 1, memo);
  const exclude = knapsack(values, weights, capacity, n - 1, memo);
  const result = Math.max(include, exclude);
  memo.set(key, result);
  return result;
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Common Patterns */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Common Recursive Patterns</h2>
            <div className="space-y-6">
              {/* Divide and Conquer */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Divide and Conquer</h3>
                  <div className="text-2xl">✂️</div>
                </div>
                <p className="text-white/70 mb-4">
                  Splits problems into smaller parts, solves them recursively, and combines results.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Merge Sort
function mergeSort(arr: number[]): number[] {
  if (arr.length <= 1) return arr;
  
  const mid = Math.floor(arr.length / 2);
  const left = mergeSort(arr.slice(0, mid));
  const right = mergeSort(arr.slice(mid));
  
  return merge(left, right);
}

function merge(left: number[], right: number[]): number[] {
  const result = [];
  let l = 0, r = 0;
  while (l < left.length && r < right.length) {
    result.push(left[l] < right[r] ? left[l++] : right[r++]);
  }
  return result.concat(left.slice(l), right.slice(r));
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Backtracking */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Backtracking</h3>
                  <div className="text-2xl">🔙</div>
                </div>
                <p className="text-white/70 mb-4">
                  Builds solutions incrementally, backtracking when a path fails.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// N-Queens solver
function solveNQueens(n: number): string[][] {
  const board = Array(n).fill(0).map(() => Array(n).fill('.'));
  const result: string[][] = [];
  
  function isSafe(row: number, col: number): boolean {
    for (let i = 0; i < col; i++) if (board[row][i] === 'Q') return false;
    for (let i = row, j = col; i >= 0 && j >= 0; i--, j--) if (board[i][j] === 'Q') return false;
    for (let i = row, j = col; i < n && j >= 0; i++, j--) if (board[i][j] === 'Q') return false;
    return true;
  }
  
  function placeQueens(col: number) {
    if (col === n) {
      result.push(board.map(row => row.join('')));
      return;
    }
    for (let row = 0; row < n; row++) {
      if (isSafe(row, col)) {
        board[row][col] = 'Q';
        placeQueens(col + 1);
        board[row][col] = '.';
      }
    }
  }
  
  placeQueens(0);
  return result;
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Performance Considerations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Performance Considerations</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Recursion's power comes with trade-offs to consider:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-4">
                <li>
                  <strong>Time Complexity:</strong> Often exponential without optimization (e.g., O(2ⁿ) for naive Fibonacci).
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`// Naive Fibonacci: O(2ⁿ)
function fib(n: number): number {
  if (n <= 1) return n;
  return fib(n - 1) + fib(n - 2);
}`}</code>
                    </pre>
                  </div>
                </li>
                <li>
                  <strong>Space Complexity:</strong> O(n) stack space for n recursive calls.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`// Stack overflow example
function deepRecursion(n: number): void {
  if (n <= 0) return;
  deepRecursion(n - 1); // Stack grows with each call
}`}</code>
                    </pre>
                  </div>
                </li>
                <li>
                  <strong>Optimization:</strong> Use tail recursion or memoization to improve efficiency.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`// Optimized with accumulator
function factorialTail(n: number, acc: number = 1): number {
  if (n <= 1) return acc;
  return factorialTail(n - 1, n * acc);
}`}</code>
                    </pre>
                  </div>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/5 rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="aspect-w-16 aspect-h-9 bg-black/50 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                >
                  <source src="https://files.catbox.moe/s8ptds.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
            </div>
          </motion.section>

          {/* Why It Matters */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Why Recursion Matters</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Elegance:</strong> Simplifies complex problems with concise code.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Natural Fit:</strong> Perfect for hierarchical or tree-like structures.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Algorithm Design:</strong> Foundation for divide-and-conquer and backtracking.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Problem Solving:</strong> Enhances ability to tackle recursive challenges.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                With recursion fundamentals in hand, you can:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Implement recursive algorithms for real problems</li>
                <li>Optimize recursive solutions with memoization</li>
                <li>Explore advanced patterns like backtracking</li>
                <li>Practice with coding challenges</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  onClick={() => navigate('/practice/recursion')}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Start Practice</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default Recursion;